import { useState } from 'react';

import { useNavigate } from 'react-router';

import { useAtomValue, useSetAtom } from 'jotai';
import { useTranslation } from 'react-i18next';

import { TextFieldType } from '@cfa/react-components';

import { useLocationChange } from '@/hooks/useLocationChange';
import { incrementSearchCountAtom } from '@/hooks/useSearchSessionTracker';

import Searchbar from 'components/Searchbar/Searchbar';
import Constants from 'constants/index';

import AISearchPOC from './AISearchPOC';

import { userInAISearchPOCAtom } from '@/atoms/ai-search-poc';
import { useSearchDispatch } from '@/atoms/search';

interface SearchbarSubheaderProps {
  onReset?: () => void;
}

const SearchbarSubheader = ({ onReset }: SearchbarSubheaderProps) => {
  const searchDispatch = useSearchDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const userInAISearchPOC = useAtomValue(userInAISearchPOCAtom);
  const incrementSearchCount = useSetAtom(incrementSearchCountAtom);
  const [searchValue, setSearchValue] = useState('');
  const [queriedValue, setQueriedValue] = useState('');

  const onChange = (e: React.ChangeEvent<TextFieldType>) => {
    setSearchValue(e.target.value);
  };

  const onClear = () => {
    setSearchValue('');
    setQueriedValue('');
    navigate('/');
  };

  const onSubmit = (suggestion?: string) => {
    onReset?.();
    incrementSearchCount();
    navigate({
      pathname: `/${Constants.ROUTE_PATH_NAMES.SEARCH_PATH_NAME}`,
      search: `query=${suggestion ?? searchValue}`,
    });
    setQueriedValue(suggestion ?? searchValue);
  };

  useLocationChange((currLocation, prevLocation) => {
    // we need to clear search input when navigating away from the search page
    if (
      prevLocation?.pathname !== currLocation?.pathname &&
      !currLocation?.pathname.includes('search')
    ) {
      setSearchValue('');
      setQueriedValue('');
    } else {
      const prevQuery = new URLSearchParams(prevLocation?.search)
        .get('query')
        ?.trim();
      const currQuery = new URLSearchParams(currLocation?.search)
        .get('query')
        ?.trim();
      // new search
      if (prevQuery && currQuery && prevQuery !== currQuery) {
        setSearchValue(currQuery);
        setQueriedValue(currQuery);
      }
      // hits search page directly
      else if (!prevQuery && currQuery) {
        searchDispatch.saveTerm(currQuery);
        setSearchValue(currQuery);
        setQueriedValue(currQuery);
      }
    }
  });

  return (
    <div className="relative mx-auto w-full">
      <Searchbar
        elevation={1}
        onChange={onChange}
        onClear={onClear}
        onSubmit={onSubmit}
        searchValue={searchValue}
        showRecentSearchDropdown
        placeholder={
          userInAISearchPOC
            ? t('AISearchPOC.searchText')
            : t('Search.searchForResources')
        }
      />
      {userInAISearchPOC && <AISearchPOC searchValue={queriedValue} />}
    </div>
  );
};

export default SearchbarSubheader;
